/**
 * O VOCABULÁRIO DOS AVISOS DO CCO — e por que um token cru na tela é defeito.
 *
 * ---------------------------------------------------------------------------
 * O QUE ESTE MÓDULO EXISTE PARA FECHAR
 *
 * `CcoPage` renderizava `data.warnings.join(" · ")` — os tokens INTERNOS, crus,
 * para o operador: `frota_fora_de_escopo`, `energia_indisponivel`. Enquanto
 * todos os avisos eram de FALHA isso era feio e transitório: na carga seguinte o
 * bloco voltava e o token sumia.
 *
 * `frota_fora_de_escopo` é o PRIMEIRO aviso PERMANENTE. Ele é o estado correto e
 * final de uma conta com escopo restrito — `fleet_snapshot` não tem chave de
 * garagem, então não existe "a frota desta garagem". O token cru passa a morar
 * na tela para sempre, e ao lado dele quatro esqueletos de KPI pulsando, que
 * dizem "espere" sobre algo que não vai mudar.
 *
 * ---------------------------------------------------------------------------
 * AS DUAS CAUSAS, E ELAS PEDEM TELAS DIFERENTES
 *
 * `falha`           a consulta estourou. TRANSITÓRIO e auto-corrigível —
 *                   recarregar resolve, e aqui esqueleto ou retry fazem sentido.
 * `impossibilidade` o recorte não existe na fonte. PERMANENTE — nenhuma espera
 *                   resolve, e prometer que resolve é o defeito que o servidor
 *                   acabou de banir do lado dele ao separar os dois tokens.
 *
 * É o mesmo par que `chargers-contrato.ts` mantém para as relações vazias, e a
 * mesma regra: **nunca uma no lugar da outra, e nunca as duas fundidas num
 * estado vazio genérico** — aí o operador aprende a ignorar as duas.
 *
 * NADA AQUI CONHECE React, DOM OU `fetch`.
 */

/** Por que o bloco não veio. */
export type CausaDeAviso = 'falha' | 'impossibilidade';

/**
 * Os tokens que `/api/cco/overview` emite em `warnings`.
 *
 * A lista é PROVADA CONTRA O SERVIDOR por teste: `cco-avisos.test.ts` varre os
 * `warnings.push('…')` de `cco-routes.ts` e exige que todo token tenha entrada
 * aqui. Um aviso novo no servidor reprova até ganhar frase — em vez de aparecer
 * cru na tela, que é exatamente o estado que este módulo encerra.
 */
export type AvisoDoCco =
  | 'frota_fora_de_escopo'
  | 'frota_indisponivel'
  | 'energia_indisponivel'
  | 'ocpp_indisponivel'
  | 'relatorios_indisponivel';

export interface Aviso {
  causa: CausaDeAviso;
  /** O que o operador lê no lugar do token, no banner de avisos. */
  frase: string;
  /**
   * A versão CURTA, para a ÁREA do bloco ausente.
   *
   * Duas frases: a longa explica a causa UMA vez, no banner; a curta ocupa o
   * lugar do conteúdo. Sem essa separação a mesma explicação de 190 caracteres
   * saía TRÊS vezes na mesma dobra (banner + KPIs + tabela) — medido na tela —,
   * e texto repetido é texto que o operador aprende a pular.
   */
  curta: string;
  /**
   * O que a ÁREA do bloco mostra quando ele não veio.
   *
   * `'esqueleto'` só para `falha`: esqueleto significa "carregando", e dizer
   * "espere" sobre uma impossibilidade permanente é a fusão que este módulo
   * existe para impedir.
   */
  tratamento: 'esqueleto' | 'frase';
}

/**
 * `Record` exaustivo sobre `AvisoDoCco`.
 *
 * Nenhuma frase tem "ainda": nas de impossibilidade o advérbio é falso, e nas de
 * falha ele é supérfluo — "tente novamente" já diz o que fazer.
 */
export const AVISO_DO_CCO: Record<AvisoDoCco, Aviso> = {
  /*
    O PRIMEIRO AVISO PERMANENTE desta tela, e a razão de o módulo existir.

    A frase NOMEIA a causa estrutural (a fonte não sabe dizer o que é seu) e NÃO
    sugere ação: não há recarregar que resolva, e não é falta de permissão —
    é falta de chave de garagem em `fleet_snapshot`. Dizer "sem permissão"
    mandaria o operador abrir chamado com o administrador errado.
  */
  frota_fora_de_escopo: {
    causa: 'impossibilidade',
    frase:
      'Os indicadores de frota não são exibidos para acessos restritos a garagem: ' +
      'a fonte de telemetria de veículo não guarda a qual garagem cada ônibus ' +
      'pertence, então não há como recortar a frota desta unidade. Não é falha, e ' +
      'recarregar não muda.',
    curta: 'Frota não recortável para acessos restritos a garagem.',
    tratamento: 'frase',
  },
  frota_indisponivel: {
    causa: 'falha',
    curta: 'Frota indisponível nesta atualização.',
    frase: 'A consulta de frota falhou nesta atualização. Os indicadores voltam na próxima.',
    tratamento: 'esqueleto',
  },
  energia_indisponivel: {
    causa: 'falha',
    curta: 'Energia indisponível nesta atualização.',
    frase: 'A consulta de energia falhou nesta atualização. O painel volta na próxima.',
    tratamento: 'esqueleto',
  },
  ocpp_indisponivel: {
    causa: 'falha',
    curta: 'Pátio de recarga indisponível nesta atualização.',
    frase: 'A consulta do pátio de recarga falhou nesta atualização. O painel volta na próxima.',
    tratamento: 'esqueleto',
  },
  relatorios_indisponivel: {
    causa: 'falha',
    curta: 'Utilização indisponível nesta atualização.',
    frase: 'A consulta de utilização falhou nesta atualização. O painel volta na próxima.',
    tratamento: 'esqueleto',
  },
};

/** Os tokens conhecidos, derivados do `Record`. Nenhuma lista à mão. */
export const AVISOS_CONHECIDOS = Object.keys(AVISO_DO_CCO) as readonly AvisoDoCco[];

function ehConhecido(token: string): token is AvisoDoCco {
  return (AVISOS_CONHECIDOS as readonly string[]).includes(token);
}

/**
 * As frases dos avisos, prontas — e o token DESCONHECIDO não vira texto.
 *
 * Um token que o front não conhece é bug de versão (servidor novo, front
 * antigo), e imprimi-lo cru é o defeito que este módulo encerra. A saída é
 * nomear o estado sem fingir que o entende.
 */
export function frasesDeAviso(warnings: readonly string[]): string[] {
  const fora: string[] = [];
  let desconhecidos = 0;
  for (const token of warnings) {
    if (ehConhecido(token)) fora.push(AVISO_DO_CCO[token].frase);
    else desconhecidos += 1;
  }
  if (desconhecidos > 0) {
    fora.push(
      desconhecidos === 1
        ? 'Um aviso desta versão do servidor não é reconhecido por esta versão do portal.'
        : `${desconhecidos} avisos desta versão do servidor não são reconhecidos por esta versão do portal.`,
    );
  }
  return fora;
}

/**
 * O bloco ausente deve mostrar ESQUELETO?
 *
 * `false` quando a ausência é permanente — e é a resposta que mata os quatro
 * esqueletos de KPI que pulsavam para sempre numa conta restrita.
 *
 * Sem aviso nenhum sobre o bloco, a resposta é `true`: aí a ausência é mesmo
 * "ainda não chegou", que é o que esqueleto significa.
 */
export function blocoAusenteEsperaDado(
  prefixo: 'frota' | 'energia' | 'ocpp' | 'relatorios',
  warnings: readonly string[],
): boolean {
  const meus = warnings.filter(
    (t): t is AvisoDoCco => ehConhecido(t) && t.startsWith(`${prefixo}_`),
  );
  if (meus.length === 0) return true;
  return meus.every((t) => AVISO_DO_CCO[t].tratamento === 'esqueleto');
}

/** A frase CURTA do bloco ausente. `null` quando não há aviso dele. */
export function fraseDoBlocoAusente(
  prefixo: 'frota' | 'energia' | 'ocpp' | 'relatorios',
  warnings: readonly string[],
): string | null {
  /*
    A MESMA ESCOLHA DE `blocoAusenteEsperaDado`, e não uma segunda.

    Ele usava `.every` e este usava `.find`: com impossibilidade E falha no
    mesmo bloco, um diria "não espere" e o outro mostraria a frase do primeiro
    da lista. Hoje é inalcançável (`cco-routes.ts` empurra um ou outro num
    `if/else if`), e por isso era latente e não defeito — mas duas leituras da
    mesma lista são duas verdades esperando a terceira linha nascer.

    A IMPOSSIBILIDADE GANHA: ela é permanente, e uma falha transitória ao lado
    dela não muda o que o operador pode fazer.
  */
  const meus = warnings.filter(
    (t): t is AvisoDoCco => ehConhecido(t) && t.startsWith(`${prefixo}_`),
  );
  if (meus.length === 0) return null;
  const impossivel = meus.find((t) => AVISO_DO_CCO[t].causa === 'impossibilidade');
  return AVISO_DO_CCO[impossivel ?? (meus[0] as AvisoDoCco)].curta;
}
